// Late-activation runtime for results + rce carousels (iteration 13).
//
// WHY: some widgets can't be decided at boot. A PDP is only identifiable from a
// dataLayer event pushed whenever the platform gets round to it (before or after
// widget.js runs), and an add-to-cart lightbox only exists AFTER its event. Both
// reduce to one operation — "draw widget W now, with data D" — fired by different
// sources: an async predicate resolving true, or an ActivationTrigger (./triggers.ts).
//
// startActivation() is the single entry the products call per widget, inside their
// existing per-widget try/catch:
//   - no `activateWhen` / sync predicate → returns the verdict; the product draws
//     during boot EXACTLY as before (BootResult unchanged);
//   - promise predicate / trigger → starts the deferred work and returns false;
//     boot() never awaits it.
// A throwing sync predicate or a throwing trigger `subscribe` still throws into the
// caller's try/catch (a boot-time error in BootResult, as before); everything that
// happens later is reported through the reporter (`onActivation` + console.error).
//
// Per fire: the draw is deferred by one macrotask (setTimeout 0), so a burst of fires
// (a double-pushed event) collapses to the LATEST, and the host's own code after its
// `push` — which often builds the very lightbox we draw into — runs first. Latest
// wins throughout: a new fire cancels this widget's previous fire still deferred or
// still waiting for its container. Then a fresh context is built (`trigger` = the
// fired data), the container is awaited via MutationObserver for at most
// `containerTimeout` (rce's getElement() throws MissingContainerError when it is
// absent after DOMContentLoaded), and the widget draws. Two draws that have BOTH
// started (their rce fetches in flight) are not coordinated: the later-resolving
// fetch renders last — out of scope (rce has no cancel/destroy API).
//
// `drawn` means "draw started": rce draws asynchronously, so a later execute-stage
// failure is reported as a separate `error` event after `drawn` (via `fail`). Re-drawing a carousel into the same
// container is safe (rce reuses and clears the existing shadowRoot); results is
// once-only (re-creating ResultsWidget into one target is unverified).

import type { ActivationCondition, ActivationContext, ActivationTrigger } from "./activation";
import { buildContext, isActivationTrigger } from "./activation";
import { normalizeTimeout } from "./triggers";

// A late (post-boot) activation outcome. `widget` uses the BootResult naming:
// "search:results" / "rce[<i>:<type>]". `drawn` = the draw STARTED (rce renders
// asynchronously; a later failure arrives as an `error` event for the same widget).
export type ActivationEvent =
  | { widget: string; kind: "drawn"; instanceId: string | null }
  | { widget: string; kind: "expired" } // the container never appeared within containerTimeout
  | { widget: string; kind: "error"; error: unknown };

export type ActivationReporter = (event: ActivationEvent) => void;

// How long a late draw waits for its container before giving up (`expired`).
// A non-finite containerTimeout (e.g. Infinity) waits indefinitely.
export const DEFAULT_CONTAINER_TIMEOUT = 10_000;

// Wrap the customer's `onActivation` so errors are always console.error'd (like
// boot-time ones) and a throwing handler can never break the pipeline.
export function createReporter(onActivation?: (event: ActivationEvent) => void): ActivationReporter {
  return (event) => {
    if (event.kind === "error") {
      console.error(`[@raventic/sdk] ${event.widget} late activation failed`, event.error);
    }
    if (!onActivation) return;
    try {
      onActivation(event);
    } catch (error) {
      console.error("[@raventic/sdk] onActivation handler threw", error);
    }
  };
}

// One widget as the runtime sees it: its name, where it draws, and how.
export interface LateWidget {
  widget: string;
  // The container to wait for (carousel `widgetConfig.targetContainerSelector` /
  // results `target`). Undefined → no wait; the draw reports its own error.
  container: string | undefined;
  containerTimeout?: number;
  // results: ignore every fire after the first draw (and stop listening).
  onceOnly?: boolean;
  // The SAME draw call boot uses, given the fire's context. Returns the instanceId
  // (rce) or null (results, which has none); throws / rejects on failure. `fail`
  // reports a failure that surfaces AFTER the draw started (rce's execute stage).
  draw(ctx: ActivationContext, fail: (error: unknown) => void): string | null | Promise<string | null>;
}

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
  (typeof value === "object" || typeof value === "function") &&
  value !== null &&
  typeof (value as { then?: unknown }).then === "function";

// true → the caller draws NOW, during boot (no condition, or a sync predicate that
// passed). false → nothing to draw now: a sync predicate said no, or the deferred
// work (promise / trigger) has been started and will draw through the pipeline.
//
// `evaluated` (iteration 21): the predicate was ALREADY called — results' `activateWhen` runs
// once, at widget.js execution (products/search.ts prepareSearch) — and this is what it
// returned; it is used instead of calling `when` again (one evaluation per page view).
export function startActivation(
  when: ActivationCondition | undefined,
  ctx: ActivationContext,
  w: LateWidget,
  report: ActivationReporter,
  evaluated?: { verdict: unknown },
): boolean {
  // Falsy (absent, null, false) → the default, true — the old resolvePredicate rule.
  if (!when) return true;

  if (isActivationTrigger(when)) {
    subscribeTrigger(when, ctx, w, report);
    return false;
  }

  const verdict = evaluated ? evaluated.verdict : when(ctx);
  if (!isThenable(verdict)) return Boolean(verdict);

  // Async predicate: not awaited by boot(); one draw when it resolves truthy.
  const fire = createFirer(w, report);
  Promise.resolve(verdict).then(
    (ok) => {
      if (ok) fire(undefined);
    },
    (error: unknown) => report({ widget: w.widget, kind: "error", error }),
  );
  return false;
}

function subscribeTrigger(
  trigger: ActivationTrigger,
  ctx: ActivationContext,
  w: LateWidget,
  report: ActivationReporter,
): void {
  let unsubscribe: (() => void) | undefined;
  let stopped = false;
  let released = false;
  const release = (): void => {
    if (!unsubscribe || released) return;
    released = true;
    try {
      unsubscribe();
    } catch (error) {
      console.error(`[@raventic/sdk] ${w.widget} trigger unsubscribe failed`, error);
    }
  };
  // Stop listening. A fire can arrive synchronously INSIDE subscribe() (a replayed
  // dataLayer entry, an element already present) — before `unsubscribe` exists — so
  // the release is retried right after subscribe() returns.
  const stop = (): void => {
    stopped = true;
    release();
  };

  const fire = createFirer(w, report, stop);
  unsubscribe = trigger.subscribe(ctx, (data?: unknown) => {
    if (stopped) return;
    // `once` is enforced here too, whatever the trigger itself does.
    if (trigger.once) stop();
    fire(data);
  });
  if (stopped) release();
}

// The per-widget draw pipeline. Holds the widget's in-flight fire (deferred / waiting)
// (latest wins) and, for once-only widgets, whether it has drawn. `onFirstDraw` runs
// when a once-only widget starts its one draw (subscribeTrigger passes `stop`).
function createFirer(
  w: LateWidget,
  report: ActivationReporter,
  onFirstDraw?: () => void,
): (data: unknown) => void {
  // Cancels this widget's in-flight fire: its deferral timer, or its container wait.
  let pending: (() => void) | null = null;
  let drawn = false;
  const fail = (error: unknown): void => report({ widget: w.widget, kind: "error", error });
  const containerTimeout =
    w.containerTimeout === undefined ? DEFAULT_CONTAINER_TIMEOUT : normalizeTimeout(w.containerTimeout);

  const drawNow = (ctx: ActivationContext): void => {
    if (w.onceOnly) {
      if (drawn) return;
      drawn = true;
      onFirstDraw?.();
    }
    try {
      const out = w.draw(ctx, fail);
      if (isThenable(out)) {
        Promise.resolve(out).then(
          (instanceId) => report({ widget: w.widget, kind: "drawn", instanceId: instanceId ?? null }),
          fail,
        );
      } else {
        report({ widget: w.widget, kind: "drawn", instanceId: out });
      }
    } catch (error) {
      fail(error);
    }
  };

  const start = (data: unknown): void => {
    pending = null;
    try {
      const ctx = buildContext(data);
      pending = waitForContainer(
        ctx.document,
        w.container,
        containerTimeout,
        () => {
          pending = null;
          drawNow(ctx);
        },
        () => {
          pending = null;
          report({ widget: w.widget, kind: "expired" });
        },
      );
    } catch (error) {
      // e.g. an invalid selector (querySelector throws SyntaxError).
      fail(error);
    }
  };

  return (data) => {
    if (w.onceOnly && drawn) return;
    // Latest wins: drop the previous fire still deferred / waiting.
    pending?.();
    const timer = setTimeout(() => start(data), 0);
    pending = () => clearTimeout(timer);
  };
}

const noop = (): void => undefined;

// Call `onReady` once `selector` matches (synchronously when it already does), or
// `onExpired` after `timeout` ms (undefined = never). Observes `childList` + `subtree` on the root and
// re-checks querySelector per mutation batch; the observer is ALWAYS disconnected
// (ready, expired or cancelled). Returns the cancel function.
export function waitForContainer(
  doc: Document,
  selector: string | undefined,
  timeout: number | undefined,
  onReady: () => void,
  onExpired: () => void,
): () => void {
  if (!selector || doc.querySelector(selector)) {
    onReady();
    return noop;
  }
  // No MutationObserver (very old browser): don't wait — the draw attempt reports
  // the missing container itself.
  if (typeof MutationObserver !== "function") {
    onReady();
    return noop;
  }

  let done = false;
  const observer = new MutationObserver(() => {
    if (done || !doc.querySelector(selector)) return;
    finish();
    onReady();
  });
  const timer =
    timeout === undefined
      ? null
      : setTimeout(() => {
          if (done) return;
          finish();
          onExpired();
        }, timeout);
  const finish = (): void => {
    done = true;
    observer.disconnect();
    if (timer !== null) clearTimeout(timer);
  };
  observer.observe(doc.documentElement, { childList: true, subtree: true });
  return () => {
    if (!done) finish();
  };
}
